import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { FileText, Download, Plus, Search, Filter, X, UploadCloud, AlertCircle, Sparkles } from 'lucide-react';
import { DOCUMENT_CATEGORIES, DocumentCategory, DocumentVisibility } from '@ftt/shared';

export const AdminDocumentsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [clientId, setClientId] = useState('');
  const [category, setCategory] = useState<DocumentCategory>('EDITORIAL');
  const [visibility, setVisibility] = useState<DocumentVisibility>('CLIENT_ONLY');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: documentsRes, isLoading } = useQuery({
    queryKey: ['admin-documents'],
    queryFn: () => api.get('/admin/documents').then((res) => res.data.data),
  });

  const { data: clientsRes } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => api.get('/admin/clients').then((res) => res.data.data),
  });

  const clients = clientsRes || [];
  const documents = documentsRes || [];

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('Please select a file.');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', title || uploadFile.name);
      formData.append('clientId', clientId || clients[0]?.id || 'clt-profile-01');
      formData.append('category', category);
      formData.append('visibility', visibility);
      return api.post('/admin/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-documents'] });
      setIsUploadOpen(false);
      setUploadFile(null);
      setTitle('');
      setErrorMsg(null);
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.error?.message || err.message || 'Upload failed.');
    },
  });

  const updateVisibilityMutation = useMutation({
    mutationFn: async ({ id, visibility }: { id: string; visibility: DocumentVisibility }) => {
      return api.put(`/admin/documents/${id}`, { visibility });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-documents'] });
    },
  });

  const filteredDocs = documents.filter((d: any) => {
    const q = searchTerm.toLowerCase();
    return (
      d.title?.toLowerCase().includes(q) ||
      d.fileName?.toLowerCase().includes(q) ||
      d.category?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header & Primary Action with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              DIGITAL ASSET VAULT
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <FileText className="w-7 h-7 text-[#FF6B4A]" />
            <span>Global Document Repository</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Manage author contracts, proofs, manuscript versions, and secure access control permissions.
          </p>
        </div>

        <button
          onClick={() => setIsUploadOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-all self-start sm:self-auto cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#FF6B4A]" />
          <span>Upload File to Client</span>
        </button>
      </div>

      {/* 2. Filter / Search Bar */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search documents by title or file name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-900 placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <span className="text-xs font-mono font-bold text-slate-600">
          Total: <strong className="text-slate-900">{documents.length}</strong> Files in Repository
        </span>
      </div>

      {/* 3. Table */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200">
          Loading documents...
        </div>
      ) : filteredDocs.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-slate-200 text-center space-y-3 shadow-sm">
          <FileText className="w-12 h-12 text-slate-400 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-900">No Documents Found</h3>
          <p className="text-xs text-slate-500 font-medium">Upload manuscripts, agreements, or editorial reports above.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200/90 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-800">
              <thead className="bg-slate-50 text-slate-700 font-mono text-[11px] uppercase border-b border-slate-200 font-bold">
                <tr>
                  <th className="px-6 py-4">File Name &amp; Title</th>
                  <th className="px-6 py-4">Client / Author</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Access Visibility</th>
                  <th className="px-6 py-4">Size &amp; Date</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-sans font-medium">
                {filteredDocs.map((doc: any) => (
                  <tr key={doc.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-extrabold text-slate-900 text-xs">{doc.title}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{doc.fileName}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-700 font-semibold">
                      {doc.client ? `${doc.client.user?.firstName || ''} ${doc.client.user?.lastName || ''}` : 'System'}
                      {doc.project && <span className="block text-[10px] text-brand-700 font-mono font-bold">[{doc.project.projectCode}]</span>}
                    </td>
                    <td className="px-6 py-4 font-mono text-[10px] uppercase font-bold text-brand-800">
                      <span className="px-2 py-0.5 rounded bg-brand-50 border border-brand-200">
                        {doc.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={doc.visibility}
                        onChange={(e) => updateVisibilityMutation.mutate({ id: doc.id, visibility: e.target.value as DocumentVisibility })}
                        className="bg-slate-50 border border-slate-200 text-[11px] font-mono font-bold rounded-lg px-2.5 py-1 text-slate-800 focus:outline-hidden cursor-pointer"
                      >
                        <option value="CLIENT_ONLY">Client Only</option>
                        <option value="ADMIN_ONLY">Admin / Staff Only</option>
                        <option value="PUBLIC">Public</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-slate-600 font-mono text-[11px] font-semibold">
                      {Math.round(doc.fileSize / 1024)} KB &bull; {new Date(doc.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 font-mono font-bold text-xs inline-flex items-center gap-1.5 border border-slate-200 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5 text-[#EA580C]" />
                        <span>Download</span>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {isUploadOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200 text-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-serif text-xl font-bold text-slate-900">Upload File to Client Repository</h3>
              <button onClick={() => setIsUploadOpen(false)} className="text-slate-400 hover:text-slate-900 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            {errorMsg && (
              <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2 font-semibold">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Assign to Client / Author
                </label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                >
                  {clients.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.user?.firstName} {c.user?.lastName} ({c.companyName || 'Independent'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Document Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Master Page Proof Vol 1"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as DocumentCategory)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  >
                    {DOCUMENT_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                    Visibility
                  </label>
                  <select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value as DocumentVisibility)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  >
                    <option value="CLIENT_ONLY">Client Only</option>
                    <option value="ADMIN_ONLY">Staff / Admin Only</option>
                    <option value="PUBLIC">Public</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  File Attachment *
                </label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-800 hover:file:bg-slate-200"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => setIsUploadOpen(false)}
                className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-bold cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => uploadMutation.mutate()}
                disabled={!uploadFile || uploadMutation.isPending}
                className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs disabled:opacity-50 cursor-pointer shadow-sm"
              >
                {uploadMutation.isPending ? 'Uploading...' : 'Upload File'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
